/**
 * childAgentReadiness — is a sub-agent this graph dispatches actually runnable?
 *
 * A dispatch node runs ANOTHER agent, and that agent dying for want of a model
 * or a key kills the parent's run too. So the parent's Trigger check follows
 * the dispatch edges rather than stopping at its own nodes.
 *
 * The child's readiness is computed from the CHILD's own row — its graph, its
 * per-node overrides, its OWN env snapshot. A node's key is snapshotted onto
 * the agent that runs it and the runtime reads that BEFORE the project
 * credential, so judging a child by the parent's credential map marks every
 * child keyless whose key lives on itself, while the child's own page shows
 * those same nodes green. Two surfaces, same question, opposite answers.
 */
import { unreadyNodes } from './nodeModel';

/** The distinct child slugs a graph dispatches, in stable order. */
export function dispatchedSlugs(graph) {
  const cfgs = graph?.nodeConfigs;
  if (!cfgs || typeof cfgs !== 'object') return [];
  const out = [];
  for (const id of Object.keys(cfgs)) {
    const slug = cfgs[id]?.dispatchesWorkflow;
    if (typeof slug === 'string' && slug && !out.includes(slug)) out.push(slug);
  }
  return out;
}

/** `{ENV_NAME: lastFour|true}` from a GET /workflows/:uuid/env response. */
export function envKeyMap(envResponse) {
  if (!envResponse) return null;
  const map = {};
  for (const e of (envResponse.entries || [])) {
    if (e && e.key) map[e.key] = e.lastFour || true;
  }
  for (const k of (envResponse.keys || [])) if (!map[k]) map[k] = true;
  return map;
}

/**
 * Is this child runnable?
 *
 * @param {object} childRow   the child's workflow row (graph, nodeConfigOverrides, marketplace)
 * @param {object} childEnv   the child's own env-key map (see envKeyMap)
 * @param {object} projectSecrets shared project credentials
 * @param {string} projectVendor  the project's default vendor
 * @returns {boolean|null} null when the child can't be judged (not deployed,
 *          unreadable) — an unknown child is never blamed.
 */
export function childIsReady(childRow, childEnv, projectSecrets, projectVendor) {
  const graph = childRow?.graph;
  if (!graph || !Array.isArray(graph.nodes) || graph.nodes.length === 0) return null;
  return unreadyNodes({
    graph,
    overrides: childRow.nodeConfigOverrides || {},
    agentEnvKeys: childEnv,
    projectSecrets,
    projectVendor,
    modelBlocks: Array.isArray(childRow.marketplace?.models) ? childRow.marketplace.models : null,
  }).length === 0;
}
